import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useAppSelector } from '@/redux/store'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import FloatingBadge, { Varients } from '@/modules/base/components/badge/floating-badge'
import { isMeinBuero } from '@/utils/build-env'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { useRevenueLimitUpgrade } from '@/modules/moduleSubscription/components/_helpers/useRevenueLimitUpgrade'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const DISMISSED_STORAGE_KEY_PREFIX = 'revenueLimitNotch:dismissed:'

const readDismissed = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (storageKey: string, dismissed: boolean) => {
  try {
    if (dismissed) {
      window.localStorage.setItem(storageKey, '1')
    } else {
      window.localStorage.removeItem(storageKey)
    }
  } catch {
    // ignore
  }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    background: `${theme.palette.primary.main} !important`,
    borderRadius: 20,
    height: 30,
    padding: '0 14px',
    position: 'static',
    [theme.devices.mobile]: {
      maxWidth: '100%',
    },
  },
  label: {
    fontSize: theme.fontSizes.smaller,
    lineHeight: theme.fontSizes.smaller,
  },
  icon: {
    fontSize: theme.fontSizes.default,
    marginRight: 6,
  },
  closeButton: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    marginLeft: 10,
    outline: 'none',
    padding: 0,
  },
  closeIcon: {
    color: theme.white,
    fontSize: theme.fontSizes.default,
  },
}), { name: 'RevenueLimitNotch' })

const RevenueLimitNotch = () => {
  const classes = useStyles()
  const userId = useAppSelector(state => state.user.info.id)
  const storageKey = `${DISMISSED_STORAGE_KEY_PREFIX}${userId}`
  const [closed, setClosed] = useState(() => readDismissed(storageKey))
  const warning = useAppSelector(state => state.subscription.details.revenueLimitWarning)
  const { upgrade, marketplaceForm } = useRevenueLimitUpgrade()

  // userId is null until the user info request resolves, so the very first read above can be
  // keyed on the wrong (null) id — re-sync once the real id (and therefore storageKey) is known.
  useEffect(() => {
    setClosed(readDismissed(storageKey))
  }, [storageKey])

  // warning.current (the locked-invoice revenue sum) only changes when an invoice is locked or
  // cancelled — there's no more direct frontend signal for "a new invoice was locked" than that.
  const previousCurrentRef = useRef(warning?.current)

  // Forget the dismissal whenever the revenue drops back under the limit (active becomes false),
  // or whenever a new invoice changes the revenue sum at all — either way the notch should show
  // fresh again rather than staying silently suppressed.
  useEffect(() => {
    const previousCurrent = previousCurrentRef.current
    previousCurrentRef.current = warning?.current

    const wentInactive = warning?.active === false
    const revenueChanged = previousCurrent !== undefined && warning?.current !== undefined && warning.current !== previousCurrent

    if (wentInactive || revenueChanged) {
      setClosed(false)
      writeDismissed(storageKey, false)
    }
  }, [warning?.active, warning?.current, storageKey])

  if (!isMeinBuero() || !warning?.active || closed) return null

  const remainingAmount = formatCurrency(Math.max(0, warning.remainingAmount))

  return (
    <>
      <FloatingBadge
        className={classes.root}
        varient={Varients.PRIMARY}
        onClick={upgrade}
      >
        <CampaignRoundedIcon className={classes.icon} />
        <span className={classes.label}>{lang.revenueLimitWarning.notchText(remainingAmount)}</span>
        <button
          type='button'
          className={classes.closeButton}
          onClick={(event) => {
            event.stopPropagation()
            setClosed(true)
            writeDismissed(storageKey, true)
          }}
          aria-label={lang.revenueLimitWarning.closeNotch}
          data-qs-id='revenue-limit-notch-close'
        >
          <CloseRoundedIcon className={classes.closeIcon} />
        </button>
      </FloatingBadge>
      {marketplaceForm}
    </>
  )
}

export default RevenueLimitNotch
